import { mainApi } from '../../../../api/mainApi';
import { AccessState } from '../../../../types';
import { middlewareApi } from '../../../middleware/middlewareApi';
import {
  ConfigProductPricePayload,
  ConfigurationProduct,
  ConfigurationProductResponse,
} from '../interfaces/configuration-product.interfaces';
import { FinancialPeriodResponse } from '../interfaces/periods-financials.interfaces';
import { ProcessResponse } from '../interfaces/process.interfaces';

import { setConfigProductList } from './configProductPriceSlice';

export const configProductsTag = middlewareApi.enhanceEndpoints({
  addTagTypes: ['configProductTag'],
});
// Define a service using a base URL and expected endpoints
export const configProductsApi = configProductsTag.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @GET all configurations products
     */
    getConfigProductsPrice: builder.query<
      ConfigurationProductResponse,
      {
        profileId: number;
        page?: number;
        perPage?: number;
        search?: string;
        sort?: string;
        type_sort?: string;
      }
    >({
      queryFn: async ({ profileId, page, search, perPage, sort, type_sort }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<ConfigurationProductResponse>('/products-config', {
            params: {
              user_profile_id: profileId,
              page,
              size: perPage,
              type_sort: type_sort || 'asc',
              sort: sort || 'id',
              search,
            },
          });

          dispatch(setConfigProductList(data?.data));

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result?.data?.map(({ id }) => ({ type: 'configProductTag', id } as const)),
              { type: 'configProductTag', id: 'LIST' },
            ]
          : [{ type: 'configProductTag', id: 'LIST' }],
    }),

    /**
     * @POST Add configuration product
     */
    addConfigProductPrice: builder.mutation<
      ConfigProductPricePayload,
      { profileId: number; configProductPayload: ConfigProductPricePayload }
    >({
      queryFn: async ({ profileId, configProductPayload }) => {
        try {
          const { data } = await mainApi.post<ConfigProductPricePayload>('/products-config', {
            user_profile_id: profileId,
            ...configProductPayload,
            pc_discount_value: configProductPayload.pc_discount_value || 0,
            pc_discount_percent: configProductPayload.pc_discount_percent || 0,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'configProductTag', id: 'LIST' }],
    }),

    /**
     * @PUT Update configuration product
     */
    editConfigProductPrice: builder.mutation<
      ConfigProductPricePayload,
      {
        productConfig: number;
        user_profile_id: number;
        configProductPayload: Partial<ConfigProductPricePayload>;
      }
    >({
      queryFn: async ({ productConfig, user_profile_id, configProductPayload }) => {
        try {
          const { data } = await mainApi.put<ConfigProductPricePayload>(
            `/products-config/${productConfig}`,
            {
              user_profile_id,
              ...configProductPayload,
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'configProductTag', id: 'LIST' }],
    }),

    /**
     * @DELETE Delete configuration product
     */
    deleteConfigProductPrice: builder.mutation<
      ConfigurationProduct,
      { productConfig: number; user_profile_id: number }
    >({
      queryFn: async ({ productConfig, user_profile_id }) => {
        try {
          const { data } = await mainApi.delete<ConfigurationProduct>(
            `/products-config/${productConfig}`,
            { data: { user_profile_id } }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'configProductTag', id: 'LIST' }],
    }),

    /**
     * @GET Obtain proccess availables
     */
    getAvailableProcess: builder.query<ProcessResponse[], { profileId: number }>({
      queryFn: async ({ profileId }) => {
        try {
          const { data } = await mainApi.get<ProcessResponse[]>('/processes', {
            params: {
              user_profile_id: profileId,
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET Obtain periods financials availables
     */
    getAvailablesPeridosFinancials: builder.query<FinancialPeriodResponse[], { profileId: number }>(
      {
        queryFn: async ({ profileId }) => {
          try {
            const { data } = await mainApi.get<FinancialPeriodResponse[]>('/financial-periods', {
              params: {
                user_profile_id: profileId,
                data: 'all',
              },
            });

            return { data: Array.isArray(data) ? data : [] };
          } catch (error: any) {
            return { error };
          }
        },
      }
    ),

    /**
     * @GET info product by id
     */
    getConfigProductPrice: builder.query<
      ConfigurationProduct,
      { profileId?: number; configProductId: number }
    >({
      queryFn: async ({ profileId, configProductId }, { dispatch, getState }) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };
        const user_profile_id = profileId || currentProfile?.profileId;

        try {
          const { data } = await mainApi.get<ConfigurationProduct>(
            `/products-config/${configProductId}`,
            {
              params: {
                user_profile_id,
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [{ type: 'configProductTag', profileId }],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetConfigProductsPriceQuery,
  useAddConfigProductPriceMutation,
  useEditConfigProductPriceMutation,
  useGetAvailableProcessQuery,
  useGetAvailablesPeridosFinancialsQuery,
  useDeleteConfigProductPriceMutation,
  useGetConfigProductPriceQuery,
  useLazyGetConfigProductPriceQuery,
} = configProductsApi;
